/**
 * MembersContext — state global des membres + églises.
 * Charge depuis l'API ou tombe en mode mock si backend indisponible.
 */
import { createContext, useState, useEffect, useCallback, useContext } from "react";
import { membersApi, eglisesApi } from "../services/api";
import { MOCK_MEMBERS, MOCK_EGLISES } from "../services/mockData";
import { AuthContext } from "./AuthContext";

export const MembersContext = createContext(null);

export function MembersProvider({ children }) {
  const auth = useContext(AuthContext);
  const [members, setMembers] = useState([]);
  const [eglises, setEglises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);

  /** Charge la liste des membres et des églises. */
  const loadAll = useCallback(async () => {
    if (!auth?.isAuthenticated) return;
    setLoading(true);
    setError(null);

    // Mode mock direct si l'auth est en mode mock
    if (auth?.usingMock) {
      setMembers(MOCK_MEMBERS);
      setEglises(MOCK_EGLISES);
      setUsingMock(true);
      setLoading(false);
      return;
    }

    try {
      const [membersRes, eglisesRes] = await Promise.all([
        membersApi.list({ per_page: 200 }),
        eglisesApi.list(),
      ]);
      setMembers(membersRes.data.items || []);
      setEglises(eglisesRes.data || []);
      setUsingMock(false);
    } catch (err) {
      // Fallback mock en cas d'erreur réseau
      const isNetwork = !err.response || err.code === "ERR_NETWORK";
      if (isNetwork) {
        setMembers(MOCK_MEMBERS);
        setEglises(MOCK_EGLISES);
        setUsingMock(true);
      } else {
        setError(err.response?.data?.detail || "Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [auth?.isAuthenticated, auth?.usingMock]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /** Ajoute un membre (optimistic, retombe au mock en cas d'erreur). */
  const addMember = useCallback(async (data) => {
    if (usingMock) {
      const newMember = {
        ...data,
        id: Math.max(0, ...members.map(m => m.id)) + 1,
        full_name: `${data.prenom} ${data.nom}`,
      };
      setMembers((prev) => [...prev, newMember]);
      return { ok: true, member: newMember };
    }
    try {
      const { data: created } = await membersApi.create(data);
      setMembers((prev) => [...prev, created]);
      return { ok: true, member: created };
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || "Erreur de création" };
    }
  }, [members, usingMock]);

  /** Met à jour un membre. */
  const updateMember = useCallback(async (id, data) => {
    if (usingMock) {
      setMembers((prev) => prev.map(m => m.id === id ? { ...m, ...data, full_name: `${data.prenom || m.prenom} ${data.nom || m.nom}` } : m));
      return { ok: true };
    }
    try {
      const { data: updated } = await membersApi.update(id, data);
      setMembers((prev) => prev.map((m) => (m.id === id ? updated : m)));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || "Erreur de mise à jour" };
    }
  }, [usingMock]);

  /** Supprime un membre. */
  const deleteMember = useCallback(async (id) => {
    if (usingMock) {
      setMembers((prev) => prev.filter(m => m.id !== id));
      return { ok: true };
    }
    try {
      await membersApi.remove(id);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || "Erreur de suppression" };
    }
  }, [usingMock]);

  const value = {
    members,
    eglises,
    loading,
    error,
    usingMock,
    refresh: loadAll,
    addMember,
    updateMember,
    deleteMember,
  };

  return <MembersContext.Provider value={value}>{children}</MembersContext.Provider>;
}
